"use client";

/**
 * CommandPalette — cmdk command menu wrapped in a dialog (focus-trapped, restored
 * on close by the underlying Radix Dialog). Filtering + "No results found." empty
 * state come from the vendored command primitive. No new Radix root.
 */
import * as React from "react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";

export interface CommandPaletteItem {
  id: string;
  label: string;
  run: () => void;
  group?: string;
  disabled?: boolean;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: CommandPaletteItem[];
  placeholder?: string;
  emptyText?: string;
  title?: string;
  description?: string;
}

function CommandPalette({
  open,
  onOpenChange,
  items,
  placeholder = "Type a command or search…",
  emptyText = "No results found.",
  title = "Command Palette",
  description = "Search for a command to run.",
}: CommandPaletteProps) {
  // Preserve the input order of groups while grouping items under their heading.
  const groups = React.useMemo(() => {
    const order: string[] = [];
    const byGroup = new Map<string, CommandPaletteItem[]>();
    for (const item of items) {
      const key = item.group ?? "";
      if (!byGroup.has(key)) {
        byGroup.set(key, []);
        order.push(key);
      }
      byGroup.get(key)!.push(item);
    }
    return order.map((key) => ({ key, items: byGroup.get(key)! }));
  }, [items]);

  function run(item: CommandPaletteItem) {
    item.run();
    onOpenChange(false);
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
    >
      <CommandInput placeholder={placeholder} />
      <CommandList>
        <CommandEmpty>{emptyText}</CommandEmpty>
        {groups.map((group) => (
          <CommandGroup
            key={group.key || "default"}
            heading={group.key || undefined}
          >
            {group.items.map((item) => (
              <CommandItem
                key={item.id}
                value={item.label}
                disabled={item.disabled}
                onSelect={() => run(item)}
              >
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}

export { CommandPalette };
